import React from 'react';
import {useTranslation} from 'react-i18next';

import {ChannelLogo} from '../../channels/ChannelLogo';
import {Target} from '../../icons';
import {
    CONVERSION_SUGGESTED, EVENTS_NOT_READABLE, EVENTS_OK, compactVolumeOf, conversionFor, conversionLabelOf, groupedOptionsOf, needsConversion,
} from '../conversionSuggestion';

// El valor del `<option>` de «la elijo después». No puede ser un id de evento: todos llevan prefijo.
const LATER = '';

/**
 * La conversión que va a monitorear cada cuenta de Meta del cliente, con la sugerencia ya puesta.
 *
 * Va a la vista y no detrás de un «ver más»: es el dato que hace que el Tablero entre midiendo
 * desde el primer minuto, y una sugerencia escondida es una sugerencia que nadie revisa. Tampoco
 * bloquea: «la elijo después» es una respuesta válida y la línea nace como nacía antes.
 *
 * Se ofrecen sólo los eventos que la cuenta REGISTRÓ en los últimos 30 días, con su volumen: los
 * resultados primero y el resto después, en su propio grupo. El catálogo entero de Meta son más de
 * cien, y elegir entre ellos sin saber cuáles usa la cuenta es justo lo que el usuario no pudo
 * resolver en el modal grande.
 */
export function ConversionPicker({group, conversionEvents, conversionsLoading, conversionEdits, dispatch,
                                  onRetry, showAccountName}) {
    const metaAccounts = group.accounts.filter(entry => needsConversion(entry.account));
    if (!metaAccounts.length) return null;
    const namesTheAccount = showAccountName === undefined ? group.accounts.length > 1 : showAccountName;

    return (
        <div className="ob-conversion">
            {metaAccounts.map(entry => (
                <ConversionLine key={entry.pk} entry={entry} showAccountName={namesTheAccount}
                                events={conversionEvents?.[String(entry.pk)]}
                                loading={conversionsLoading} conversionEdits={conversionEdits}
                                dispatch={dispatch} onRetry={onRetry}/>
            ))}
        </div>
    );
}

function ConversionLine({entry, showAccountName, events, loading, conversionEdits, dispatch, onRetry}) {
    const {t, i18n} = useTranslation();
    const language = i18n?.language;
    const {actionIds, source} = conversionFor({pk: entry.pk, conversionEvents: {[String(entry.pk)]: events},
                                               conversionEdits});
    const answered = events?.status === EVENTS_OK;
    const {results, others} = groupedOptionsOf(answered ? events.actions : []);
    const current = actionIds[0];
    // La elección guardada tiene que estar entre las opciones aunque la cuenta ya no la registre (el
    // borrador sobrevive a una recarga y el volumen se vuelve a pedir). Sin esto el `<select>`
    // mostraría el primer evento de la lista mientras el alta manda otro.
    const orphan = current && ![...results, ...others].some(action => action.action_id === current)
        ? {action_id: current, counts: null} : null;
    const hasOptions = results.length + others.length > 0 || orphan;
    const selected = [...results, ...others, ...(orphan ? [orphan] : [])]
        .find(action => action.action_id === current);

    const partsOf = action => ({
        name: conversionLabelOf(action, language),
        amount: action.counts ? compactVolumeOf(action.counts.last_30_days, language) : null,
    });

    const optionOf = action => {
        const {name, amount} = partsOf(action);
        return (
            <option key={action.action_id} value={action.action_id}>
                {amount === null ? name
                    : t('onboarding.budgets.conversion_option', {name, amount, interpolation: {escapeValue: false}})}
            </option>
        );
    };

    return (
        <div className="ob-conversion__line">
            <span className="ob-conversion__label">
                <Target size={13}/>
                {showAccountName && (
                    <ChannelLogo channel={entry.account.channel} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                )}
                <span className="ob-conversion__label-text">
                    {showAccountName
                        ? t('onboarding.budgets.conversion_label_account',
                            {account: entry.account.name || entry.account.account_id,
                             interpolation: {escapeValue: false}})
                        : t('onboarding.budgets.conversion_label')}
                </span>
            </span>

            <span className="ob-conversion__control">
                {!events && loading ? (
                    <span className="ob-conversion__status" role="status">
                        <span className="ob-spinner-mini"/> {t('onboarding.budgets.conversion_loading')}
                    </span>
                ) : events?.status === EVENTS_NOT_READABLE && !orphan ? (
                    /* Dashbo no puede leer esta cuenta (C-CONVCUENTA-03). No es culpa de Meta ni se
                       arregla reintentando, así que no se ofrece. */
                    <span className="ob-conversion__status">{t('onboarding.budgets.conversion_not_readable')}</span>
                ) : !answered && !orphan ? (
                    /* Meta no contestó (o el pedido entero se cayó). No es lo mismo que una cuenta sin
                       conversiones, y se puede reintentar sin recargar la página. */
                    <span className="ob-conversion__status -with-action">
                        {t('onboarding.budgets.conversion_unavailable')}
                        {onRetry && (
                            <>
                                {' '}
                                <button type="button" className="ob-link" onClick={onRetry}>
                                    {t('onboarding.budgets.conversion_retry')}
                                </button>
                            </>
                        )}
                    </span>
                ) : !hasOptions ? (
                    <span className="ob-conversion__status">{t('onboarding.budgets.conversion_none')}</span>
                ) : (
                    <>
                        {/* El `<select>` nativo muestra la opción elegida recortada por la derecha, y
                            en un nombre largo lo que se pierde es el volumen (QA-conv3-07). Encima va
                            su propia etiqueta: el nombre se recorta y el volumen queda siempre. El
                            select sigue siendo el control, con su texto invisible. */}
                        <span className="ob-conversion__field">
                            <select className="ob-conversion__select" value={current || LATER}
                                    aria-label={t('onboarding.budgets.conversion_label')}
                                    onChange={event => dispatch({
                                        type: 'EDIT_CONVERSION', pk: entry.pk,
                                        actionIds: event.target.value === LATER ? [] : [event.target.value],
                                    })}>
                                {results.length > 0 && (
                                    <optgroup label={t('onboarding.budgets.conversion_group_results')}>
                                        {results.map(optionOf)}
                                    </optgroup>
                                )}
                                {others.length > 0 && (
                                    <optgroup label={t('onboarding.budgets.conversion_group_others')}>
                                        {others.map(optionOf)}
                                    </optgroup>
                                )}
                                {orphan && optionOf(orphan)}
                                <option value={LATER}>{t('onboarding.budgets.conversion_later')}</option>
                            </select>
                            <SelectedConversion parts={selected ? partsOf(selected) : null}
                                                laterLabel={t('onboarding.budgets.conversion_later')}/>
                        </span>
                        {source === CONVERSION_SUGGESTED && (
                            <span className="ob-conversion__suggested">
                                {t('onboarding.budgets.conversion_suggested')}
                            </span>
                        )}
                    </>
                )}
            </span>
        </div>
    );
}

/** Lo que se ve dentro del selector: el nombre, que se recorta, y el volumen, que no. */
function SelectedConversion({parts, laterLabel}) {
    return (
        <span className="ob-conversion__display" aria-hidden="true">
            <span className="ob-conversion__display-name">{parts ? parts.name : laterLabel}</span>
            {parts?.amount != null && <span className="ob-conversion__display-volume">{parts.amount}</span>}
        </span>
    );
}
